/**
 * Single-open accordion for the four AI Coach insight blocks.
 * Owns its open-id locally. The header is always visible; the body collapses
 * with a smooth max-height transition.
 *
 * The block bodies are passed in as children (one <AccordionItem> per block)
 * so AICoachDrawer can keep the existing block components in place.
 */

import React, { useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { PURPLE } from './ai-theme';

interface AccordionItemProps {
  id: string;
  title: string;
  icon: React.ReactNode;
  badge?: React.ReactNode;
  isOpen: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}

export const AccordionItem: React.FC<AccordionItemProps> = ({
  id, title, icon, badge, isOpen, onToggle, children,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef(false);
  // 'auto' | '<px>' | '0px'. We animate between 0 and the measured scrollHeight,
  // then settle on 'auto' once open so dynamically-loaded content can still grow
  // (the old `max-height: 1000px` clamp silently truncated long blocks).
  const [maxHeight, setMaxHeight] = useState<string>(isOpen ? 'auto' : '0px');

  useLayoutEffect(() => {
    // Skip on initial mount — initial state already matches isOpen and we don't
    // want to flash through a numeric height before settling.
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    const el = contentRef.current;
    if (!el) return;
    if (isOpen) {
      // closed → open: lock to current scrollHeight to drive the transition.
      // onTransitionEnd releases back to 'auto' once the animation finishes.
      setMaxHeight(`${el.scrollHeight}px`);
    } else {
      // open → closed: if we were at 'auto', first pin to the current measured
      // height so the transition has a numeric starting value, then drop to 0
      // on the next frame.
      const h = el.scrollHeight;
      setMaxHeight(`${h}px`);
      requestAnimationFrame(() => setMaxHeight('0px'));
    }
  }, [isOpen]);

  const onTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.propertyName !== 'max-height') return;
    if (isOpen) setMaxHeight('auto');
  };

  return (
    <div className="border border-stone-200 rounded-xl overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={isOpen}
        className="w-full flex items-center gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-stone-600 hover:bg-stone-50"
      >
        <span style={{ color: PURPLE }} className="shrink-0">{icon}</span>
        <span className="flex-1 text-left">{title}</span>
        {badge}
        <ChevronDown
          className={`w-3.5 h-3.5 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        ref={contentRef}
        className="overflow-hidden transition-[max-height] duration-200 ease-out"
        style={{ maxHeight }}
        onTransitionEnd={onTransitionEnd}
      >
        <div className="p-3 border-t border-stone-200">{children}</div>
      </div>
    </div>
  );
};

interface InsightAccordionProps {
  defaultOpenId?: string;
  children: (renderItem: {
    isOpen: (id: string) => boolean;
    toggle: (id: string) => void;
  }) => React.ReactNode;
}

export const InsightAccordion: React.FC<InsightAccordionProps> = ({
  defaultOpenId = 'quality',
  children,
}) => {
  const [openId, setOpenId] = useState<string | null>(defaultOpenId);
  const isOpen = (id: string) => openId === id;
  const toggle = (id: string) => setOpenId((current) => (current === id ? null : id));
  return <div className="space-y-2">{children({ isOpen, toggle })}</div>;
};
